import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { useProfileReleases } from '../useProfileReleases';
import { useProfileMake } from '../useProfileMake';
import { profileMessages as m } from '../profileMessages';
import { ProfileReleaseGrid } from '../tools/ProfileReleaseGrid';

interface ReleasePickerDialogProps {
  accountId: number;
  isOpen: boolean;
  /** The release picked first: the newest release. */
  initialUuid: string | undefined;
  onClose: () => void;
}

/**
 * "Use a release" (design a1, a2 "Pick another release", c3 note): the releases with a cover,
 * newest first. "Use it" makes the Spotify header and the two banners from the blurred cover of
 * the release, in its look (`POST .../artist-profile/from-release`). Free.
 */
const ReleasePickerDialogComponent: React.FC<ReleasePickerDialogProps> = ({
  accountId,
  isOpen,
  initialUuid,
  onClose,
}) => {
  const intl = useIntl();
  const { releases } = useProfileReleases(accountId);
  const make = useProfileMake(accountId);
  // The pick of this opening; until the artist picks, the newest release (or the first of the list).
  const [choice, setChoice] = useState<string | undefined>(undefined);
  const picked = choice ?? initialUuid ?? releases[0]?.catalogItemUuid;
  const { clearFeedback } = make;
  useEffect(() => {
    if (isOpen) {
      setChoice(undefined);
      clearFeedback();
    }
  }, [clearFeedback, isOpen]);

  const confirm = () => {
    if (picked === undefined) {
      return;
    }
    make
      .fromRelease(picked)
      .then((done) => {
        if (done) {
          onClose();
        }
      })
      .catch(console.error);
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="neutral"
      title={intl.formatMessage(m.pickerTitle)}
      description={intl.formatMessage(m.pickerBody)}
      confirmLabel={intl.formatMessage(m.pickerConfirm)}
      cancelLabel={intl.formatMessage(m.pickerCancel)}
      isBusy={make.isBusy}
      isConfirmDisabled={picked === undefined}
      error={make.feedback?.message}
      onConfirm={confirm}
      onCancel={onClose}
      testId="profile-release-picker"
    >
      {releases.length === 0 ? (
        <p className="text-muted small mb-0">{intl.formatMessage(m.pickerEmpty)}</p>
      ) : (
        <ProfileReleaseGrid
          releases={releases}
          value={picked}
          label={intl.formatMessage(m.pickerTitle)}
          onPick={setChoice}
        />
      )}
    </ConfirmDialog>
  );
};

export const ReleasePickerDialog = TypedMemo(ReleasePickerDialogComponent);
